import { AccessControlProvider } from "@refinedev/core";
import { AxiosInstance } from "axios";
import { RoleEnum } from "types/roles";

export const ROLE_KEY = "emr-role";

export enum ResourceEnum {
  Dashboard = "dashboard",
  SubMenuSettings = "emr-settings",
  SubMenuOperations = "emr-operations",
  SubMenuFleet = "emr-fleet",
  SubMenuVesselServices = "emr-vessel-services",
  GlobalSettings = "global-settings",
  UploadVesselPermits = "upload-vessel-permits",
  UploadSeallPermits = "upload-seall-permits",
  UploadFetchPermits = "upload-fetch-permits",
  UploadOrders = "upload-orders",
  OrderUploadStatus = "order-upload-status",
  Order = "order",
  Shipco = "shipco",
  ShipcoGroup = "shipco-group",
  Licence = "licence",
  Vessel = "vessel",
  Ecdis = "ecdis",
  SubMenuSubscription = "emr-subscription",
  Subscription = "subscription",
  MetadataAvcs = "metadata-avcs",
  MetadataAdp = "metadata-adp",
  MetadataEnp = "metadata-enp",
  MetadataArcs = "metadata-arcs",
  MetadataOther = "metadata-other",
  SubMenuReport = "emr-report",
  ReportVesselSpend = "vessel-spend-report",
  ReportInvoice = "invoice-report",
  PhysicalBooks = "physical-books",
  VesselCertificates = "vessel-certificates",
  SubMenuBookshelf = "emr-bookshelf",
  DigitalPublication = "digital-publication",
  FlagCircular = "flag-circular",
  NoticesMariners = "notices-mariners",
  NavWarnings = "nav-warnings",
  BaseData = "base-data",
  PermitFile = "permit-file",
  Address = "address",
  Contact = "contact",
  Country = "country",
  Currency = "currency",
  VesselResources = "vessel-resources",
  VesselStarterKit = "vessel-startup-kit",
  DataTransfer = "data-transfer",
  AvcsIncrementalUpdate = "upload-avcs-incremental-update",
  VesselAccounts = "vessel-accounts",
  EnpData = "enp-data",
  UkhoInvoiceReport = "ukho-invoice-report",
  OnDemandReports = "on-demand-reports",
  VesselTransfer = "vessel-transfer",
  OrderReport = "order-report",
  VesselEmailSettings = "vessel-email-settings",
  AuditLogs = "audit-logs",
  FlatFeeAnalytics = "flat-fee-analytics",
  UkhoTransferNotice = "ukho-transfer-notice",
}

export enum ActionEnum {
  List = "list",
  Create = "create",
  Edit = "edit",
  Show = "show",
  Delete = "delete",
}

export const accessControlProvider = (
  axiosInstance: AxiosInstance
): AccessControlProvider => {
  return {
    can: async ({ resource, action, params }) => {
      const role = localStorage.getItem(ROLE_KEY) || "";

      // Deleting order upload records is locked to DistributorSuperAdmin
      if (
        resource === ResourceEnum.OrderUploadStatus &&
        action === ActionEnum.Delete &&
        role !== RoleEnum.DistributorSuperAdmin
      ) {
        return Promise.resolve({
          can: false,
          reason: "Unauthorized",
        });
      }

      if (
        resource === ResourceEnum.FlatFeeAnalytics &&
        role !== RoleEnum.DistributorManagement &&
        role !== RoleEnum.DistributorSuperAdmin &&
        role !== RoleEnum.DistributorAdmin
      ) {
        return Promise.resolve({ can: false, reason: "Unauthorized" });
      }

      // UKHO transfer notices (vessel block, three Vessel Transfers tabs) are
      // for the three roles the API lets in; every other role is denied
      // before the role blocks below.
      if (
        resource === ResourceEnum.UkhoTransferNotice &&
        role !== RoleEnum.DistributorManagement &&
        role !== RoleEnum.DistributorSuperAdmin &&
        role !== RoleEnum.DistributorAdmin
      ) {
        return Promise.resolve({ can: false, reason: "Unauthorized" });
      }

      /**
       * permissions for DISTRIBUTOR SUPER ADMIN role
       */
      if (role === RoleEnum.DistributorSuperAdmin) {
        if (resource === ResourceEnum.Order && action === ActionEnum.Create) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
        if (resource === ResourceEnum.Vessel && action === ActionEnum.Delete) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
        if (
          resource === ResourceEnum.Subscription &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
      }

      /**
       * permissions for DISTRIBUTOR ADMIN  role
       */
      if (role === RoleEnum.DistributorAdmin) {
        if (
          resource === ResourceEnum.Order &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
        if (resource === ResourceEnum.Vessel && action === ActionEnum.Delete) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
        if (
          resource === ResourceEnum.Subscription &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
      }

      /**
       * permissions for DISTRIBUTOR USER  role
       */
      if (role === RoleEnum.DistributorUser) {
        if (
          resource === ResourceEnum.Order &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
        if (resource === ResourceEnum.UploadOrders || resource === ResourceEnum.OrderUploadStatus) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
        if (resource === ResourceEnum.Shipco && action === ActionEnum.Delete) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.ShipcoGroup &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.Licence && action === ActionEnum.Delete) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.Vessel && action === ActionEnum.Delete) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.Ecdis && action === ActionEnum.Delete) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.Subscription &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.MetadataAvcs &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.MetadataAdp &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.MetadataArcs &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.MetadataEnp &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.MetadataOther &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.Address && action === ActionEnum.Delete) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.Contact && action === ActionEnum.Delete) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.Country && action === ActionEnum.Delete) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.Currency &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.PhysicalBooks &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.VesselCertificates &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.BaseData &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.GlobalSettings) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.DigitalPublication &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.EnpData &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.FlagCircular &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.NoticesMariners &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.NavWarnings &&
          action === ActionEnum.Delete
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.AvcsIncrementalUpdate &&
          action === ActionEnum.Delete
        ) { 
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.VesselAccounts) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.UkhoInvoiceReport) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.OrderReport) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.OnDemandReports) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.SubMenuReport) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (resource === ResourceEnum.VesselTransfer) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
      }

      /**
       * permissions for SHIPCO USER and SHICO GROUP USER  role
       */
      if (role === RoleEnum.ShipcoUser || role === RoleEnum.ShipcoGroupUser) {
        if (
          resource === ResourceEnum.Order &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.PhysicalBooks &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.VesselCertificates &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.BaseData &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.DigitalPublication &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.EnpData &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.FlagCircular &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.NoticesMariners &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.NavWarnings &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.AvcsIncrementalUpdate &&
          action === ActionEnum.Delete
        ) { 
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.UploadVesselPermits ||
          resource === ResourceEnum.UploadSeallPermits ||
          resource === ResourceEnum.UploadFetchPermits ||
          resource === ResourceEnum.UploadOrders ||
          resource === ResourceEnum.OrderUploadStatus ||
          resource === ResourceEnum.SubMenuFleet ||
          resource === ResourceEnum.Shipco ||
          resource === ResourceEnum.ShipcoGroup ||
          resource === ResourceEnum.Licence ||
          resource === ResourceEnum.Vessel ||
          resource === ResourceEnum.Ecdis ||
          resource === ResourceEnum.SubMenuSubscription ||
          resource === ResourceEnum.Subscription ||
          resource === ResourceEnum.MetadataAdp ||
          resource === ResourceEnum.MetadataArcs ||
          resource === ResourceEnum.MetadataAvcs ||
          resource === ResourceEnum.MetadataEnp ||
          resource === ResourceEnum.MetadataOther ||
          resource === ResourceEnum.SubMenuReport ||
          resource === ResourceEnum.SubMenuSettings ||
          resource === ResourceEnum.VesselEmailSettings ||
          resource === ResourceEnum.Address ||
          resource === ResourceEnum.Contact ||
          resource === ResourceEnum.Country ||
          resource === ResourceEnum.Currency ||
          resource === ResourceEnum.GlobalSettings ||
          resource === ResourceEnum.AuditLogs ||
          resource === ResourceEnum.ReportInvoice ||
          resource === ResourceEnum.VesselAccounts ||
          resource === ResourceEnum.UkhoInvoiceReport ||
          resource === ResourceEnum.OrderReport ||
          resource === ResourceEnum.OnDemandReports ||
          resource === ResourceEnum.VesselTransfer
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
      }

      /**
       * permissions for VESSEL END USER  role
       */
      if (role === RoleEnum.VesselUser) {
        // if (
        //   resource === ResourceEnum.Order &&
        //   (action === ActionEnum.Create ||
        //     action === ActionEnum.Edit ||
        //     action === ActionEnum.Delete)
        // ) {
        //   return Promise.resolve({
        //     can: false,
        //     reason: "Unauthorized",
        //   });
        // }

        // if (
        //   resource === ResourceEnum.PhysicalBooks &&
        //   (action === ActionEnum.Create ||
        //     action === ActionEnum.Edit ||
        //     action === ActionEnum.Delete)
        // ) {
        //   return Promise.resolve({
        //     can: false,
        //     reason: "Unauthorized",
        //   });
        // }

        // if (
        //   resource === ResourceEnum.VesselCertificates &&
        //   (action === ActionEnum.Create ||
        //     action === ActionEnum.Edit ||
        //     action === ActionEnum.Delete)
        // ) {
        //   return Promise.resolve({
        //     can: false,
        //     reason: "Unauthorized",
        //   });
        // }

        // if (
        //   resource === ResourceEnum.BaseData &&
        //   (action === ActionEnum.Create ||
        //     action === ActionEnum.Edit ||
        //     action === ActionEnum.Delete)
        // ) {
        //   return Promise.resolve({
        //     can: false,
        //     reason: "Unauthorized",
        //   });
        // }

        if (
          resource === ResourceEnum.DigitalPublication &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.EnpData &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
        
        if (
          resource === ResourceEnum.FlagCircular &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.NoticesMariners &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.NavWarnings &&
          (action === ActionEnum.Create ||
            action === ActionEnum.Edit ||
            action === ActionEnum.Delete)
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.AvcsIncrementalUpdate &&
          action === ActionEnum.Delete
        ) { 
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }

        if (
          resource === ResourceEnum.SubMenuOperations ||
          resource === ResourceEnum.UploadVesselPermits ||
          resource === ResourceEnum.UploadSeallPermits ||
          resource === ResourceEnum.UploadFetchPermits ||
          resource === ResourceEnum.UploadOrders ||
          resource === ResourceEnum.OrderUploadStatus ||
          resource === ResourceEnum.Order ||
          resource === ResourceEnum.SubMenuFleet ||
          resource === ResourceEnum.Shipco ||
          resource === ResourceEnum.ShipcoGroup ||
          resource === ResourceEnum.Licence ||
          resource === ResourceEnum.Vessel ||
          resource === ResourceEnum.Ecdis ||
          resource === ResourceEnum.SubMenuSubscription ||
          resource === ResourceEnum.Subscription ||
          resource === ResourceEnum.MetadataAdp ||
          resource === ResourceEnum.MetadataArcs ||
          resource === ResourceEnum.MetadataAvcs ||
          resource === ResourceEnum.MetadataEnp ||
          resource === ResourceEnum.MetadataOther ||
          resource === ResourceEnum.SubMenuReport ||
          resource === ResourceEnum.PhysicalBooks ||
          resource === ResourceEnum.VesselCertificates ||
          resource === ResourceEnum.SubMenuSettings ||
          resource === ResourceEnum.VesselEmailSettings ||
          resource === ResourceEnum.Address ||
          resource === ResourceEnum.Contact ||
          resource === ResourceEnum.Country ||
          resource === ResourceEnum.Currency ||
          resource === ResourceEnum.GlobalSettings ||
          resource === ResourceEnum.AuditLogs ||
          resource === ResourceEnum.ReportVesselSpend ||
          resource === ResourceEnum.ReportInvoice ||
          resource === ResourceEnum.BaseData ||
          resource === ResourceEnum.PermitFile ||
          resource === ResourceEnum.VesselAccounts ||
          resource === ResourceEnum.UkhoInvoiceReport ||
          resource === ResourceEnum.OrderReport ||
          resource === ResourceEnum.OnDemandReports ||
          resource === ResourceEnum.VesselTransfer
        ) {
          return Promise.resolve({
            can: false,
            reason: "Unauthorized",
          });
        }
      }
      return Promise.resolve({ can: true });
    },
  };
};
